<template>
  <div
    v-if="visible"
    class="modal-backdrop"
    @click.self="$emit('close')"
  >
    <div class="modal event-info-modal">
      <!-- Botón X en esquina superior derecha -->
      <button class="close-btn" @click="$emit('close')">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <h3>Información del Evento</h3>

      <div
        v-if="eventInfo"
        class="event-details"
      >
        <div class="detail-row">
          <label>ID:</label>
          <span>{{ eventInfo.id }}</span>
        </div>

        <div class="detail-row">
          <label>Empresa Cliente:</label>
          <span>{{ eventInfo.empresa_cliente }}</span>
        </div>

        <div class="detail-row">
          <label>Custodia:</label>
          <span>{{ eventInfo.custodia }}</span>
        </div>

        <div class="detail-row">
          <label>Envase:</label>
          <span>{{ eventInfo.envase }}</span>
        </div>

        <div class="detail-row">
          <label>Formato:</label>
          <span>{{ eventInfo.formato }}</span>
        </div>

        <div class="detail-row">
          <label>Peso Estimado:</label>
          <span>{{ eventInfo.peso_estimado }}</span>
        </div>

        <div class="detail-row">
          <label>Duración:</label>
          <div
            v-if="allowDurationEdit"
            class="duration-select-wrapper"
          >
            <select
              class="duration-select"
              :value="localDuration"
              @change="onDurationChange($event.target.value)"
            >
              <option
                v-for="option in durationOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <span v-else>{{ formatDuration(eventInfo?.duration_minutes) }}</span>
        </div>

        <div class="detail-row">
          <label>Planta Proceso:</label>
          <div
            v-if="allowProcessPlantEdit"
            class="plant-select-wrapper"
          >
            <select
              class="plant-select"
              :value="localProcessPlant"
              @change="onProcessPlantChange($event.target.value)"
            >
              <option
                v-for="option in processPlantOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
          <span v-else>{{ eventInfo.planta_proceso }}</span>
        </div>

        <!-- Sección de Notas - Solo visible si showNotes es true -->
        <div v-if="showNotes" class="notes-section reduced-margin">
          <div class="detail-row notes-header">
            <label>Notas ventas:</label>
          </div>
          <div class="notes-input-container">
            <textarea
              :value="eventInfo.notes"
              @input="updateNotes($event.target.value)"
              class="notes-textarea"
              rows="3"
              placeholder="Agregar notas..."
            />
          </div>
        </div>

        <!-- Sección de Ingredientes -->
        <div class="ingredients-section">
          <div class="detail-row ingredients-header clickable" @click="toggleIngredients">
            <label>Ingredientes:</label>
            <div class="ingredients-toggle">
              <span
                v-if="eventInfo.ingredients.length === 0"
                class="loading-text"
              >Cargando...</span>
              <span v-else class="ingredient-count">{{ eventInfo.ingredients.length }} ingredientes</span>
              <svg
                class="chevron-icon"
                :class="{ 'rotated': showIngredients }"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path d="M6 9l6 6 6-6" stroke="#6b7280" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <div
            v-if="eventInfo.ingredients.length > 0 && showIngredients"
            class="ingredients-list"
          >
            <div
              v-for="ingredient in eventInfo.ingredients"
              :key="ingredient.id"
              class="ingredient-item"
            >
              <span class="ingredient-name">{{ ingredient.name }}</span>
              <span class="ingredient-percentage">{{ ingredient.percentage }}%</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Acciones -->
      <div class="modal-actions">
        <button
          class="save-btn"
          @click="handleSave"
        >
          Guardar
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EventInfoModal',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    eventInfo: {
      type: Object,
      default: null,
    },
    allowProcessPlantEdit: {
      type: Boolean,
      default: false,
    },
    processPlantOptions: {
      type: Array,
      default: () => [],
    },
    allowDurationEdit: {
      type: Boolean,
      default: false,
    },
    minDuration: {
      type: Number,
      default: 15,
    },
    maxDuration: {
      type: Number,
      default: 240,
    },
    durationStep: {
      type: Number,
      default: 15,
    },
    showNotes: {
      type: Boolean,
      default: true,
    },
  },
  emits: [
    'close',
    'save',
    'update-notes',
    'update-process-plant',
    'update-duration',
  ],
  data() {
    return {
      showIngredients: false,
      localProcessPlant: null,
      localDuration: null,
    };
  },
  watch: {
    eventInfo: {
      immediate: true,
      handler(newInfo) {
        this.localProcessPlant = newInfo?.process_plant || null;
        const normalizedDuration = this.normalizeDurationValue(newInfo?.duration_minutes);
        this.localDuration = this.clampDuration(normalizedDuration ?? this.minDuration);
      },
    },
  },
  methods: {
    updateNotes(value) {
      this.$emit('update-notes', value);
    },
    toggleIngredients() {
      this.showIngredients = !this.showIngredients;
    },
    onProcessPlantChange(value) {
      this.localProcessPlant = value;
      this.$emit('update-process-plant', value);
    },
    onDurationChange(value) {
      const minutes = this.normalizeDurationValue(value);
      if (minutes === null) return;
      const clamped = this.clampDuration(minutes);
      this.localDuration = clamped;
      this.$emit('update-duration', clamped);
    },
    formatDuration(minutes) {
      const normalized = this.normalizeDurationValue(minutes);
      if (normalized === null) return 'N/A';
      const hours = Math.floor(normalized / 60);
      const mins = normalized % 60;
      if (hours > 0) {
        return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
      }
      return `${mins}m`;
    },
    normalizeDurationValue(value) {
      if (value === null || value === undefined || value === '') return null;
      if (typeof value === 'number') {
        return Number.isFinite(value) ? Math.round(value) : null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) ? Math.round(parsed) : null;
    },
    clampDuration(value) {
      if (value === null) return this.minDuration;
      const step = this.durationStep || 15;
      const min = this.minDuration || 15;
      const max = this.maxDuration || 240;
      const clamped = Math.min(max, Math.max(min, value));
      return Math.round(clamped / step) * step;
    },
    handleSave() {
      this.$emit('save');
      // Don't emit 'close' here - let the parent component close after successfully saving
    },
  },
  computed: {
    durationOptions() {
      const options = [];
      const step = this.durationStep || 15;
      const min = this.minDuration || 15;
      const max = this.maxDuration || 240;
      for (let minutes = min; minutes <= max; minutes += step) {
        options.push({
          value: minutes,
          label: this.formatDuration(minutes),
        });
      }
      return options;
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
  text-align: center;
}

.event-info-modal {
  max-width: 500px;
  text-align: left;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.event-info-modal h3 {
  margin-top: 0;
  color: #2c3e50;
  margin-bottom: 20px;
  text-align: center;
}

.event-details {
  max-height: calc(80vh - 120px);
  overflow-y: auto;
  padding-right: 8px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f1f3f4;
  font-size: 13px;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row label {
  font-weight: 600;
  color: #495057;
  min-width: 120px;
}

.detail-row span {
  color: #495057;
  text-align: right;
}

.plant-select-wrapper {
  width: 100%;
  text-align: right;
}

.plant-select {
  width: 160px;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: #374151;
  background-color: #fff;
}

.duration-select-wrapper {
  width: 100%;
  text-align: right;
}

.duration-select {
  width: 160px;
  padding: 5px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: #374151;
  background-color: #fff;
}

.notes-section {
  margin-top: 15px;
  border-top: 2px solid #e9ecef;
  padding-top: 15px;
}

.notes-section.reduced-margin {
  margin-top: 8px;
  padding-top: 8px;
}

.notes-header {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 10px;
  padding-bottom: 8px;
}

.notes-header label {
  font-weight: bold;
  font-size: 14px;
  color: #495057;
}

.notes-input-container {
  padding-left: 10px;
}

.notes-textarea {
  width: 100%;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  font-family: 'Source Sans Pro', sans-serif;
  color: #495057;
  resize: vertical;
  min-height: 60px;
}

.notes-textarea:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.ingredients-section {
  margin-top: 15px;
}

.ingredients-header {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 10px;
  padding-bottom: 8px;
}

.loading-text {
  color: #6c757d;
  font-style: italic;
  font-size: 12px;
}

.ingredients-list {
  padding-left: 10px;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 8px;
}

.ingredients-list::-webkit-scrollbar {
  width: 8px;
}

.ingredients-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.12);
  border-radius: 4px;
}

.ingredients-list::-webkit-scrollbar-track {
  background: transparent;
}

.ingredient-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #f1f3f4;
  font-size: 13px;
}

.ingredient-item:last-child {
  border-bottom: none;
}

.ingredient-name {
  color: #495057;
  flex: 1;
  text-align: left;
}

.ingredient-percentage {
  color: #28a745;
  font-weight: 600;
  min-width: 50px;
  text-align: right;
}

.modal-actions {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 10px;
}

.modal-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  transition: background-color 0.2s ease;
}

.modal-actions .save-btn {
  background: #2c3e50;
  color: #fff;
  padding: 10px 20px;
  font-size: 14px;
}

.modal-actions .save-btn:hover {
  background: #34495e;
}

/* Botón X en esquina superior derecha */
.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.close-btn path {
  transition: stroke 0.2s ease;
}

.close-btn:hover path {
  stroke: #ffffff;
}

/* Estilos para ingredientes colapsables */
.ingredients-header.clickable {
  cursor: pointer;
  user-select: none;
}

.ingredients-header.clickable:hover {
  background-color: #f8f9fa;
}

.ingredients-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ingredient-count {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}

.chevron-icon {
  transition: transform 0.2s ease;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}
</style>
